/**
 * Staging powers itself down outside business hours to save cost — the
 * EC2 app host + RDS stop on a schedule (every day 08:00–20:00 America/
 * Los_Angeles; down every night). See
 * `infra/terraform/envs/staging/scheduler.tf`.
 *
 * When the box is asleep the portal can't reach the API, so these helpers
 * let it show a friendly "staging is asleep" notice instead of a raw
 * connection error. The notice must only ever fire on the staging
 * deployment — dev runs locally and prod is 24/7.
 */

/** Human-readable window, shown in the off-hours notice. */
export const STAGING_HOURS_LABEL = 'every day, 8 am–8 pm Pacific';

/**
 * True only on the staging deployment (`staging.10newtons.net`). Matches
 * the bare host and any `staging.*` so a future staging subdomain still
 * works; localhost (dev) and `app.10newtons.net` (prod) return false.
 */
export function isStagingHost(
  host: string = window.location.hostname,
): boolean {
  return host === 'staging.10newtons.net' || host.startsWith('staging.');
}

/**
 * True when `now` falls outside staging's business hours, evaluated in
 * Pacific time regardless of the viewer's own timezone. Business hours
 * are 08:00–20:00 every day, weekends included; nights are "outside".
 */
export function isOutsideStagingHours(now: Date = new Date()): boolean {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Los_Angeles',
    hour: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(now);

  const hour = Number(parts.find((p) => p.type === 'hour')?.value ?? '0');
  return hour < 8 || hour >= 20;
}
